import type { LucideIcon } from 'lucide-react'

import {
    Crop,
    FileImage,
    Layers,
    Minimize2,
    Ratio,
    RefreshCw,
    Scaling,
    ShieldOff,
    Smartphone
} from 'lucide-react'

export type ToolCategory = 'Images' | 'PDFs'

export interface ToolNote {
    heading: string
    text: string
}

export interface ToolLink {
    label: string
    href: string
}

export interface ToolInfo {
    slug: string
    name: string
    short: string
    category: ToolCategory
    icon: LucideIcon
    seoTitle: string
    description: string
    heading: string
    intro: string
    notes: ToolNote[]
    related: ToolLink[]
}

export const TOOLS: ToolInfo[] = [
    {
        slug: 'compress-image',
        name: 'Shrink image',
        short: 'Smaller files.',
        category: 'Images',
        icon: Minimize2,
        seoTitle: 'Compress image to 100KB, 200KB, 500KB or 1MB',
        description: 'Compress an image to a target size like 100KB, 200KB, 500KB or 1MB. Free, no account, and your image stays in your browser.',
        heading: 'Compress an image to the size you actually need',
        intro: 'Choose a maximum KB or MB target. The tool works toward that limit in your browser.',
        notes: [
            { heading: 'Made for upload limits', text: 'Use it when a website, form, marketplace, app store, email or social platform rejects a file for being too big. Enter the maximum size allowed and the tool aims for it.' },
            { heading: 'How close will it get?', text: 'Image encoders cannot produce every possible file size, so the tool looks for the best looking result that stays at or under your target. For very small targets it may also reduce the dimensions to get under the limit.' },
            { heading: 'Size limit and dimensions together?', text: 'If a site asks for both, resize or crop first, then compress. That way no bytes are spent on pixels that will never be shown.' }
        ],
        related: [
            { label: 'Compress to 100KB', href: '/compress-image?size=100&unit=KB' },
            { label: 'Image resizer', href: '/resize-image' },
            { label: 'Format converter', href: '/convert-image' },
            { label: 'Compression guide', href: '/image-compression-guide' }
        ]
    },
    {
        slug: 'resize-image',
        name: 'Resize',
        short: 'Set the dimensions.',
        category: 'Images',
        icon: Scaling,
        seoTitle: 'Resize image to exact pixels',
        description: 'Resize an image to exact pixel dimensions like 1080 × 1080 or 1920 × 1080 without stretching. Free and private in your browser.',
        heading: 'Resize an image without stretching it',
        intro: 'Set the width and height in pixels. Keep the shape, crop to fill, or stretch, and see the result before you save.',
        notes: [
            { heading: 'Keep shape, crop or stretch?', text: 'Keeping the shape fits the image inside your size. Cropping fills the exact size and trims the edges. Stretching forces the size and can warp faces, circles and text.' },
            { heading: 'Making an image bigger', text: 'Enlarging creates new in-between pixels. It can make the file larger, but it cannot add detail that was not in the original.' },
            { heading: 'File size comes afterward', text: 'The right dimensions do not guarantee a KB or MB limit. Compress the resized image if you also need a smaller file.' }
        ],
        related: [
            { label: 'Resize to 1080 × 1080', href: '/resize-image?w=1080&h=1080' },
            { label: 'Aspect ratio calculator', href: '/aspect-ratio' },
            { label: 'Crop image', href: '/crop-image' },
            { label: 'Resizing guide', href: '/image-resizing-guide' }
        ]
    },
    {
        slug: 'convert-image',
        name: 'Change format',
        short: 'Choose a file type.',
        category: 'Images',
        icon: RefreshCw,
        seoTitle: 'Convert images to JPG, PNG or WebP',
        description: 'Convert images between JPG, PNG and WebP for free in your browser. HEIC, GIF and other formats can be converted too.',
        heading: 'Change an image to JPG, PNG or WebP',
        intro: 'Pick the format the destination asks for. Your image is converted right here in your browser.',
        notes: [
            { heading: 'Which format should I pick?', text: 'JPG is the safe choice for photos. PNG suits logos, screenshots and anything with sharp text or transparency. WebP makes small files for websites.' },
            { heading: 'Transparency and JPG', text: 'JPG cannot store transparent areas, so they are filled with the background color you choose.' },
            { heading: 'Converting does not restore quality', text: 'Turning a JPG into a PNG does not bring back detail the JPG already discarded. It only stops further loss.' }
        ],
        related: [
            { label: 'PNG to JPG', href: '/convert-image?to=jpg' },
            { label: 'JPG to PNG', href: '/convert-image?to=png' },
            { label: 'JPG to WebP', href: '/convert-image?to=webp' },
            { label: 'Format guide', href: '/image-format-guide' }
        ]
    },
    {
        slug: 'heic-to-jpg',
        name: 'HEIC to JPG',
        short: 'Make HEIC easier to share.',
        category: 'Images',
        icon: Smartphone,
        seoTitle: 'Convert HEIC to JPG',
        description: 'Convert iPhone HEIC photos to JPG for free. Convert one or many at once, right in your browser, with no upload.',
        heading: 'Turn iPhone HEIC photos into JPGs',
        intro: 'HEIC saves space on your phone, but many websites and computers will not open it. Convert one photo or a whole batch to JPG.',
        notes: [
            { heading: 'Why HEIC?', text: 'iPhones save photos as HEIC by default because the files are smaller. JPG is far more widely accepted by websites, forms and older computers.' },
            { heading: 'Large batches', text: 'Converting happens on your device, so big batches can take a little while. Keep this page open until every photo is done.' },
            { heading: 'Hidden details', text: 'The converted JPGs do not keep the stored details from the original, such as camera and location information.' }
        ],
        related: [
            { label: 'Compress the JPG', href: '/compress-image' },
            { label: 'Resize the JPG', href: '/resize-image' },
            { label: 'Photos to PDF', href: '/images-to-pdf' }
        ]
    },
    {
        slug: 'crop-image',
        name: 'Crop',
        short: 'Keep the part you need.',
        category: 'Images',
        icon: Crop,
        seoTitle: 'Crop image online',
        description: 'Crop an image to a square, portrait, story or widescreen shape. Free and private in your browser.',
        heading: 'Crop an image to the shape you need',
        intro: 'Pick a shape, drag the image into place and zoom in if needed. Only the part inside the frame is kept.',
        notes: [
            { heading: 'Choose the shape first', text: 'Square suits profile photos and many listings. Portrait 4:5 suits feeds, 9:16 suits stories, and 16:9 suits banners and video thumbnails.' },
            { heading: 'Crop, then resize', text: 'Cropping keeps the original sharpness of the part you keep. Resize afterwards if the destination asks for exact pixels.' }
        ],
        related: [
            { label: 'Resize image', href: '/resize-image' },
            { label: 'Aspect ratio calculator', href: '/aspect-ratio' },
            { label: 'Resizing guide', href: '/image-resizing-guide' }
        ]
    },
    {
        slug: 'images-to-pdf',
        name: 'Photos to PDF',
        short: 'One PDF from your photos.',
        category: 'PDFs',
        icon: FileImage,
        seoTitle: 'Combine photos into one PDF',
        description: 'Turn photos, scans and screenshots into a single PDF. Reorder pages, pick A4 or Letter, and keep files private in your browser.',
        heading: 'Turn your photos into one PDF',
        intro: 'Add photos or scans, put them in order, and create a single PDF that is easy to send and print.',
        notes: [
            { heading: 'Upright and in order', text: 'Check that every page faces the right way and sits in the right order before you create the PDF.' },
            { heading: 'Too big to send?', text: 'Choose the smaller file option, or crop away desks and shadows first. Text stays readable at every setting.' },
            { heading: 'Check before sharing', text: 'Open the saved PDF and look at each page to make sure nothing is missing, repeated or cut off.' }
        ],
        related: [
            { label: 'Crop image', href: '/crop-image' },
            { label: 'HEIC to JPG', href: '/heic-to-jpg' },
            { label: 'Photos to PDF guide', href: '/images-to-pdf-guide' },
            { label: 'Edit the PDF with DOCSCRUB', href: '/docscrub' }
        ]
    },
    {
        slug: 'remove-image-metadata',
        name: 'Remove metadata',
        short: 'Remove stored image details.',
        category: 'Images',
        icon: ShieldOff,
        seoTitle: 'Remove EXIF and image metadata',
        description: 'Remove EXIF, GPS location and other hidden details from photos before you share them. Free and private in your browser.',
        heading: 'Remove hidden details before you share a photo',
        intro: 'Photos can carry the camera model, the date and sometimes the exact location. Make a clean copy that leaves them behind.',
        notes: [
            { heading: 'What gets removed', text: 'EXIF details such as camera, date, settings and GPS location, plus XMP, IPTC, comments and text notes stored in the file.' },
            { heading: 'Quality stays the same', text: 'For JPG and PNG files, the picture itself is left untouched. Only the hidden details are taken out.' },
            { heading: 'What it cannot hide', text: 'Anything visible in the picture, such as an address, a name badge or a number plate, is still there. Crop or cover it separately.' }
        ],
        related: [
            { label: 'Metadata guide', href: '/image-metadata-privacy-guide' },
            { label: 'Crop image', href: '/crop-image' },
            { label: 'Clean PDFs with DOCSCRUB', href: '/docscrub' }
        ]
    },
    {
        slug: 'batch-resize-images',
        name: 'Resize many',
        short: 'One size for many images.',
        category: 'Images',
        icon: Layers,
        seoTitle: 'Batch resize images',
        description: 'Resize many images to the same size at once and save them as one zip file. Free and private in your browser.',
        heading: 'Resize many images to one size',
        intro: 'Choose a batch of images, set one size for all of them, and save them one by one or together in a zip file.',
        notes: [
            { heading: 'Same size, different shapes', text: 'Keep shape fits each image inside your size. Crop to fill gives every image the exact same dimensions by trimming edges.' },
            { heading: 'Big batches', text: 'Everything runs on your device. Up to 50 images can be resized at a time, so larger sets can be split into a few rounds.' }
        ],
        related: [
            { label: 'Resize one image', href: '/resize-image' },
            { label: 'Compress image', href: '/compress-image' },
            { label: 'Resizing guide', href: '/image-resizing-guide' }
        ]
    },
    {
        slug: 'aspect-ratio',
        name: 'Aspect ratio',
        short: 'Find the right proportions.',
        category: 'Images',
        icon: Ratio,
        seoTitle: 'Aspect ratio calculator',
        description: 'Find the aspect ratio of any size, scale it to a new width or height, and see how it fits common frames like 1080 × 1080.',
        heading: 'Find the right proportions',
        intro: 'Work out an aspect ratio, keep it at a new size, and see how your image fits common frames before you upload.',
        notes: [
            { heading: 'What aspect ratio means', text: 'It is the relationship between width and height. 1920 × 1080 and 1280 × 720 are different sizes with the same 16:9 shape.' },
            { heading: 'Nothing to upload', text: 'This calculator only works with numbers. No image is needed.' }
        ],
        related: [
            { label: 'Resize image', href: '/resize-image' },
            { label: 'Crop image', href: '/crop-image' },
            { label: 'Resizing guide', href: '/image-resizing-guide' }
        ]
    }
]

export const findTool = (slug: string): ToolInfo | undefined => TOOLS.find((tool) => tool.slug === slug)
